import { Divider } from '@/components/divider';
import classes from '@/components/downloadCard/DownloadCard.module.scss';
import DownloadItem from '@/components/downloadCard/DownloadItem/DownloadItem';
import { assetSizeForHumans, getAssetExtension, getAssetTitle, getAssetUrl, isAIGeneratedAsset } from '@/helper/asset';
import type { AssetStoryblok, PartialDownloadCategoryStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';

export type BaseDownloadCardProps = {
  title?: string;
  image?: AssetStoryblok;
  alt?: string;
  className?: string;
} & ({ files: PartialDownloadCategoryStoryblok[] } | { file: AssetStoryblok }) & {
    size?: 'small' | 'medium' | 'large';
    'data-blok-c'?: string;
    'data-blok-uid'?: string;
  };

export type StaticDownloadFileRecord = {
  title: string;
  file: string;
};

export const BaseDownloadCard: React.FC<BaseDownloadCardProps> = (props) => {
  if (!('file' in props) && !('files' in props)) {
    return;
  }

  if ('file' in props) {
    const singleFile = props.file;

    return (
      <aside className={classNames(classes['download-card'], classes.single)} data-testid="download-card">
        <DownloadItem
          title={getAssetTitle(singleFile)}
          file={getAssetUrl(singleFile)}
          size={
            singleFile.resolved_meta_data && singleFile.resolved_meta_data.content_length
              ? assetSizeForHumans(singleFile.resolved_meta_data.content_length)
              : undefined
          }
          extension={getAssetExtension(singleFile)}
          variant="single"
          image={props.image}
          imageAlt={props.alt}
          isAIGenerated={isAIGeneratedAsset(singleFile)}
        />
      </aside>
    );
  }

  if ('files' in props) {
    return (
      <aside className={classNames(classes['download-card'], props.className)} data-testid="download-card">
        {props.title && (
          <h3
            className={classNames(classes['download-card__title'], 'download-card__title')}
            data-testid="download-card-title"
          >
            {props.title}
          </h3>
        )}
        {props.files.map((category: PartialDownloadCategoryStoryblok) => (
          <div className={classes['download-card__category']} data-testid="download-card-category" key={category._uid}>
            {category.title && (
              <>
                <p
                  className={classNames(classes['download-card__category-title'], 'font-label-bold')}
                  data-testid="download-card-category-title"
                >
                  {category.title}
                </p>
                <Divider className={classes['download-card__category-divider']} data-testid="download-card-divider" />
              </>
            )}

            {category.files && (
              <ul
                className={classNames(
                  classes['download-card__category-list'],
                  classes[`download-card__category-list-size-${props.size}`]
                )}
                data-testid="download-card-list"
              >
                {category.files?.map((file, index) => (
                  <li
                    data-testid="download-card-list-item"
                    className={classes['download-card__category-list-item']}
                    key={`${file.id}-${index}`}
                  >
                    <DownloadItem
                      title={getAssetTitle(file)}
                      file={getAssetUrl(file)}
                      size={
                        file.resolved_meta_data && file.resolved_meta_data.content_length
                          ? assetSizeForHumans(file.resolved_meta_data.content_length)
                          : undefined
                      }
                      extension={getAssetExtension(file)}
                      variant="multiple"
                      isAIGenerated={isAIGeneratedAsset(file)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </aside>
    );
  }
};

export default BaseDownloadCard;
